import { useAuthFetchWithResult } from '@e3dc-react/shell/Hooks/AuthFetch'

import { CountryInfoDto } from './dto/country-info.dto'
import { usePortalQuery } from 'src/Libs/api'

/**
 * Function for fetching country info data
 * - this API contains list of countries with VAT rate for hager-bridge service
 * - we need to match if installation country is in list of countries inside this API
 * - if installation country is not in API list, we will inform user about it with proper banner
 * - this hook is used inside emobility wallboxes, charging sessions and tariffs
 * @returns country info data as array of CountryInfoDto, isCountryInfoLoading as boolean and isCountryInfoError as boolean
 */
interface UseCountryInfoHook {
  countryInfo: CountryInfoDto[]
  isCountryInfoLoading: boolean
  isCountryInfoError: boolean
  country: CountryInfoDto | undefined
}
export const useFetchCountryInfo = (
  installationCountryCode: string,
  installationId: number | undefined,
  isInstallationDeleted: boolean
): UseCountryInfoHook => {
  const authFetch = useAuthFetchWithResult()

  const fetchCountryInfo = async (): Promise<CountryInfoDto[]> => {
    return authFetch<CountryInfoDto[]>({
      path: `country-info/${installationId}`,
      endpoint: process.env.REACT_APP_HAGER_BRIDGE_SERVICE,
      method: 'GET',
    })
  }

  const { data, isLoading, isError } = usePortalQuery({
    queryFn: fetchCountryInfo,
    queryKey: ['countryInfo'],
    refetchOnWindowFocus: false,
    refetchOnMount: 'always',
    enabled: Boolean(installationId) && !isInstallationDeleted,
  })

  /**
   * Check if response is actually an array before applying the transformation. Better use zod for a runtime check if
   * response fits the DTO interface.
   *
   * This check is the only such check in the codebase since it's causing a crash of the whole frontend when there is
   * a missing env variable for HagerBridge URL. In that case, but only locally, the error message from the dev server
   * is returned as an HTML string, therefore response exists, but cannot be processed properly.
   */
  const country = Array.isArray(data) ? data.find(country => country.installationCountryCode === installationCountryCode) : undefined

  return {
    countryInfo: data ?? [],
    isCountryInfoLoading: isLoading,
    isCountryInfoError: isError,
    country,
  }
}
